import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Spin, Alert, Space, Button, List } from 'antd';
import {
  BankOutlined,
  TeamOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import superAdminApi, { OverviewStats, AuditLog } from '../../api/superAdminApi';

const { Title, Text } = Typography;

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewRes, activityRes] = await Promise.all([
        superAdminApi.getOverview(),
        superAdminApi.getRecentActivity(10)
      ]);

      if (overviewRes.success) {
        setStats(overviewRes.data);
      }

      if (activityRes.success) {
        setRecentActivity(activityRes.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action: string): string => {
    if (action.includes('CREATE')) return 'green';
    if (action.includes('UPDATE') || action.includes('OVERRIDE')) return 'blue';
    if (action.includes('DELETE') || action.includes('SUSPEND')) return 'red';
    if (action.includes('ACTIVATE') || action.includes('REACTIVATE')) return 'cyan';
    return 'default';
  };

  const activityColumns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'Admin',
      dataIndex: 'adminName',
      key: 'adminName',
      width: 150,
      render: (name: string, record: AuditLog) => name || record.adminEmail
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => (
        <Tag color={getActionColor(action)}>
          {action.replace(/_/g, ' ')}
        </Tag>
      )
    },
    {
      title: 'Target',
      dataIndex: 'targetType',
      key: 'targetType',
      render: (type: string) => <Tag>{type}</Tag>
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button onClick={fetchData}>Retry</Button>
        }
      />
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <div style={{ padding: '12px' }}>
        <Title level={4} style={{ marginBottom: 8 }}>Super Admin</Title>
        <Text type="secondary" style={{ fontSize: 12 }}>Overview of companies, users & subscriptions</Text>

        {/* Stats Cards - 2 column grid on mobile */}
        <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card size="small" hoverable onClick={() => navigate('/admin/companies')} bodyStyle={{ padding: 12 }}>
              <Statistic
                title={<span style={{ fontSize: 11 }}>Companies</span>}
                value={stats?.companies.total || 0}
                prefix={<BankOutlined />}
                valueStyle={{ fontSize: 20 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>
                {stats?.companies.active || 0} active
              </Text>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" hoverable onClick={() => navigate('/admin/users')} bodyStyle={{ padding: 12 }}>
              <Statistic
                title={<span style={{ fontSize: 11 }}>Users</span>}
                value={stats?.users.total || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ fontSize: 20 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>
                {stats?.users.active || 0} active
              </Text>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" hoverable onClick={() => navigate('/admin/subscriptions')} bodyStyle={{ padding: 12 }}>
              <Statistic
                title={<span style={{ fontSize: 11 }}>Active Subs</span>}
                value={stats?.subscriptions.active || 0}
                prefix={<CreditCardOutlined />}
                valueStyle={{ fontSize: 20, color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" hoverable onClick={() => navigate('/admin/invoices')} bodyStyle={{ padding: 12 }}>
              <Statistic
                title={<span style={{ fontSize: 11 }}>Pending Invoices</span>}
                value={stats?.invoices.pending || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ fontSize: 20, color: stats?.invoices.pending ? '#faad14' : undefined }}
              />
            </Card>
          </Col>
        </Row>

        {/* Alert Cards */}
        {((stats?.subscriptions.pastDue || 0) > 0 || (stats?.subscriptions.expiringSoon || 0) > 0) && (
          <div style={{ marginTop: 12 }}>
            {(stats?.subscriptions.pastDue || 0) > 0 && (
              <Alert
                message={`${stats?.subscriptions.pastDue} Past Due`}
                type="error"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 8 }}
                action={
                  <Button size="small" danger onClick={() => navigate('/admin/subscriptions?status=PAST_DUE')}>
                    View
                  </Button>
                }
              />
            )}
            {(stats?.subscriptions.expiringSoon || 0) > 0 && (
              <Alert
                message={`${stats?.subscriptions.expiringSoon} Expiring Soon`}
                type="warning"
                showIcon
                icon={<ClockCircleOutlined />}
                action={
                  <Button size="small" onClick={() => navigate('/admin/subscriptions')}>
                    View
                  </Button>
                }
              />
            )}
          </div>
        )}

        {/* Quick Actions - horizontal scroll */}
        <Card size="small" title="Quick Actions" style={{ marginTop: 12 }} bodyStyle={{ padding: 8 }}>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            <Button size="small" type="primary" onClick={() => navigate('/admin/companies')}>
              <BankOutlined /> Companies
            </Button>
            <Button size="small" onClick={() => navigate('/admin/users')}>
              <TeamOutlined /> Users
            </Button>
            <Button size="small" onClick={() => navigate('/admin/plans')}>
              Plans
            </Button>
            <Button size="small" onClick={() => navigate('/admin/subscriptions')}>
              Subscriptions
            </Button>
            <Button size="small" onClick={() => navigate('/admin/invoices')}>
              Invoices
            </Button>
            <Button size="small" onClick={() => navigate('/admin/audit')}>
              Audit
            </Button>
          </div>
        </Card>

        {/* Recent Activity - List instead of Table */}
        <Card
          size="small"
          title="Recent Activity"
          style={{ marginTop: 12 }}
          bodyStyle={{ padding: 0 }}
          extra={
            <Button type="link" size="small" onClick={() => navigate('/admin/audit')}>
              All <ArrowRightOutlined />
            </Button>
          }
        >
          <List
            size="small"
            dataSource={recentActivity.slice(0, 5)}
            locale={{ emptyText: 'No recent activity' }}
            renderItem={(item) => (
              <List.Item style={{ padding: '8px 12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tag color={getActionColor(item.action)} style={{ fontSize: 10 }}>
                      {item.action.replace(/_/g, ' ')}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {new Date(item.createdAt).toLocaleTimeString()}
                    </Text>
                  </div>
                  <Text style={{ fontSize: 12 }}>{item.adminName || item.adminEmail}</Text>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Super Admin Dashboard</Title>
      <Text type="secondary">Overview of all companies, users, and subscriptions</Text>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/companies')}>
            <Statistic
              title="Total Companies"
              value={stats?.companies.total || 0}
              prefix={<BankOutlined />}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({stats?.companies.active || 0} active)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/users')}>
            <Statistic
              title="Total Users"
              value={stats?.users.total || 0}
              prefix={<TeamOutlined />}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({stats?.users.active || 0} active)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/subscriptions')}>
            <Statistic
              title="Active Subscriptions"
              value={stats?.subscriptions.active || 0}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/admin/invoices')}>
            <Statistic
              title="Pending Invoices"
              value={stats?.invoices.pending || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: stats?.invoices.pending ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Alert Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {(stats?.subscriptions.pastDue || 0) > 0 && (
          <Col xs={24} sm={12}>
            <Alert
              message="Past Due Subscriptions"
              description={`${stats?.subscriptions.pastDue} subscriptions are past due and require attention.`}
              type="error"
              showIcon
              icon={<WarningOutlined />}
              action={
                <Button size="small" danger onClick={() => navigate('/admin/subscriptions?status=PAST_DUE')}>
                  View <ArrowRightOutlined />
                </Button>
              }
            />
          </Col>
        )}
        {(stats?.subscriptions.expiringSoon || 0) > 0 && (
          <Col xs={24} sm={12}>
            <Alert
              message="Expiring Soon"
              description={`${stats?.subscriptions.expiringSoon} subscriptions will expire within 30 days.`}
              type="warning"
              showIcon
              icon={<ClockCircleOutlined />}
              action={
                <Button size="small" onClick={() => navigate('/admin/subscriptions')}>
                  View <ArrowRightOutlined />
                </Button>
              }
            />
          </Col>
        )}
      </Row>

      {/* Quick Links */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title="Quick Actions">
            <Space wrap>
              <Button type="primary" onClick={() => navigate('/admin/companies')}>
                <BankOutlined /> Manage Companies
              </Button>
              <Button onClick={() => navigate('/admin/users')}>
                <TeamOutlined /> Manage Users
              </Button>
              <Button onClick={() => navigate('/admin/plans')}>
                <CreditCardOutlined /> Manage Plans
              </Button>
              <Button onClick={() => navigate('/admin/subscriptions')}>
                Manage Subscriptions
              </Button>
              <Button onClick={() => navigate('/admin/invoices')}>
                <FileTextOutlined /> View Invoices
              </Button>
              <Button onClick={() => navigate('/admin/audit')}>
                Audit Logs
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            title="Recent Activity"
            extra={
              <Button type="link" onClick={() => navigate('/admin/audit')}>
                View All <ArrowRightOutlined />
              </Button>
            }
          >
            <Table
              columns={activityColumns}
              dataSource={recentActivity}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No recent activity' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
